export const AUTH_RETURN_KEY = "tarahumAuthReturnTo";
export const UAE_PASS_REQUIRED_KEY = "tarahumUaePassRequired";

const TOKEN_KEYS = ["token", "accessToken", "authToken", "jwt"];

export const getStoredToken = () => {
  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (const key of TOKEN_KEYS) {
      const token = storage.getItem(key);
      if (token) return token;
    }
  }
  return "";
};

const getTokenExpiry = (token) => {
  try {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = payload.padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const expiry = Number(JSON.parse(window.atob(paddedPayload)).exp);
    return Number.isFinite(expiry) ? expiry : null;
  } catch (error) {
    return null;
  }
};

export const isTokenExpired = (token = getStoredToken()) => {
  const expiry = getTokenExpiry(token);
  return expiry !== null && expiry <= Math.floor(Date.now() / 1000);
};

export const isAuthenticated = () => {
  const token = getStoredToken();
  return Boolean(token) && !isTokenExpired(token);
};

export const setAuthReturnPath = (path) => {
  const safePath = typeof path === "string" && path.startsWith("/") && !path.startsWith("//")
    ? path
    : "/aid-application";
  window.sessionStorage.setItem(AUTH_RETURN_KEY, safePath);
};

export const clearAuthReturnPath = () => {
  window.sessionStorage.removeItem(AUTH_RETURN_KEY);
};

export const getPendingAuthReturnPath = () => {
  const path = window.sessionStorage.getItem(AUTH_RETURN_KEY);
  return path && path.startsWith("/") && !path.startsWith("//") ? path : "";
};

export const getAuthReturnPath = (fallback = "/aid-application") => {
  return getPendingAuthReturnPath() || fallback;
};

export const consumeAuthReturnPath = (fallback = "/aid-application") => {
  const path = getAuthReturnPath(fallback);
  window.sessionStorage.removeItem(AUTH_RETURN_KEY);
  return path;
};

export const getAccountDashboardPath = (accountType) => {
  return String(accountType || "").trim().toLowerCase() === "donor"
    ? "/donor-dashboard.html"
    : "/beneficent-dashboard";
};

export const navigateToAuthDestination = (path) => {
  const destination = path && path.startsWith("/") && !path.startsWith("//")
    ? path
    : "/";

  if (/dashboard\.html(?:[?#]|$)/i.test(destination)) {
    window.location.assign(destination);
    return;
  }

  window.history.pushState({}, "", destination);
  window.dispatchEvent(new Event("app:navigate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
};

export const storeAuthResult = (result, remember = true, loginMethod = "manual") => {
  if (result.token) {
    const selectedStorage = remember ? window.localStorage : window.sessionStorage;
    const otherStorage = remember ? window.sessionStorage : window.localStorage;
    TOKEN_KEYS.forEach((key) => {
      selectedStorage.setItem(key, result.token);
      otherStorage.removeItem(key);
    });
  }

  const user = {
    user: result.user,
    userId: result.userId,
    userName: result.userName,
    loginMethod,
    profile: result.profile || null,
  };
  window.localStorage.setItem("tarahumUser", JSON.stringify(user));
  if (isUaePassConnected(user)) {
    window.sessionStorage.removeItem(UAE_PASS_REQUIRED_KEY);
  }
  window.dispatchEvent(new Event("auth:changed"));
  return user;
};

export const getStoredUser = () => {
  try {
    const value = window.localStorage.getItem("tarahumUser");
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

export const isUaePassConnected = (user = getStoredUser()) => {
  const profile = user?.profile || {};
  const loginMethod = String(user?.loginMethod || "").trim().toLowerCase();
  const registrationSource = String(profile.regfrom || profile.registrationSource || "").trim().toLowerCase();
  return loginMethod === "uae-pass"
    || loginMethod === "uaepass"
    || registrationSource === "uae-pass"
    || registrationSource === "uaepass"
    || profile.isVerified === true
    || profile.isUaePass === true
    || Boolean(profile.uaepassid || profile.uaePassId || profile.uae_pass_id);
};

export const clearStoredAuth = () => {
  TOKEN_KEYS.forEach((key) => {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  });
  window.localStorage.removeItem("tarahumUser");
  window.dispatchEvent(new Event("auth:changed"));
};

export const redirectToLogin = () => {
  const isArabic = window.location.pathname.toLowerCase().startsWith("/ar/");
  clearStoredAuth();
  window.history.replaceState({}, "", isArabic ? "/ar/login" : "/login");
  window.dispatchEvent(new Event("app:navigate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
};
